import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal, { ModalRef } from 'components/Modal/Modal';
import AGTableWrapper from 'components/AGTable/AGTableWrapper';
import Loader from 'components/Loader/Loader';
import AGMessage from 'components/AGMessage/AGMessage';
import AGSimpleSelect, { SelectOption } from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';
import Pagination from 'components/Pagination/Pagination';
import { slotClient } from 'clients';
import { BulkRentalSlotsFilter, SLOT_PAGE_SIZE } from 'clients/SlotClient';
import { TenantsParkingData } from 'types';
import { SlotInterface } from 'types';
import { PagedResponse } from 'types/Pagination';
import { STATUS_LABEL, deriveBulkSlotDisplayStatus } from './utils';
import type { BulkSlotDisplayStatus } from './utils';
import styles from './TenantBulkSlotsModal.module.css';
import SearchInput from 'components/AGForm/Inputs/SearchInput/SearchInput';
import { formatPlates } from 'utils/helpers';

type TenantBulkSlotsModalProps = {
    tenant: TenantsParkingData;
    onClose: () => void;
    isDemoMode?: boolean;
};

const FILTER_OPTIONS: SelectOption[] = [
    { value: '', label: 'All Drivers' },
    { value: BulkRentalSlotsFilter.NotEnded, label: 'Active' },
    { value: BulkRentalSlotsFilter.Ended, label: 'Ended' },
];

const statusBadgeColorMap: Record<BulkSlotDisplayStatus, string> = {
    invited: 'statusInvited',
    active: 'statusActive',
    inactive: 'statusInactive',
};

// In demo mode keep the first name but reduce the last name to its initial.
function displayLastName(lastName: string | null | undefined, isDemoMode: boolean): string {
    if (!lastName) return '—';
    return isDemoMode ? `${lastName.charAt(0)}.` : lastName;
}

const TenantBulkSlotsModal = ({ tenant, onClose, isDemoMode = false }: TenantBulkSlotsModalProps) => {
    const modalRef = useRef<ModalRef>(null);
    const [page, setPage] = useState(1);
    const [filter, setFilter] = useState<BulkRentalSlotsFilter>(BulkRentalSlotsFilter.NotEnded);
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [paged, setPaged] = useState<PagedResponse<SlotInterface> | null>(null);

    useEffect(() => {
        modalRef.current?.openModal();
    }, []);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(searchInput), 400);
        return () => clearTimeout(handle);
    }, [searchInput]);

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setErrorMessage('');
        (async () => {
            try {
                const res = await slotClient.queryBulkRentals({
                    bulkRentalUuid: tenant.uuid,
                    page,
                    pageSize: SLOT_PAGE_SIZE,
                    searchTerm: debouncedSearch || undefined,
                    filter,
                });
                if (!cancelled) {
                    setPaged(res);
                }
            } catch (e) {
                if (!cancelled) {
                    setErrorMessage(e instanceof Error ? e.message : 'Failed to load reserved spots.');
                    setPaged(null);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [tenant.uuid, page, debouncedSearch, filter]);

    const displayRows = useMemo(() => paged?.results ?? [], [paged]);

    const totalPages = useMemo(() => Math.ceil((paged?.count ?? 0) / SLOT_PAGE_SIZE), [paged?.count]);

    function handleFilterSelect(value: string) {
        setFilter(value as BulkRentalSlotsFilter);
        setPage(1);
    }

    const title = tenant.description || '';

    return (
        <Modal ref={modalRef} title={title} onClose={onClose}>
            <div className={styles.body}>
                <div className={styles.toolbar}>
                    <h2>Drivers</h2>

                    <SearchInput
                        onChange={(e) => setSearchInput(e.target.value)}
                        value={searchInput}
                        placeholder="Search Driver"
                        aria-label="Search Driver"
                        type="text"
                        className={styles.toolbarInput}
                    />
                    <AGSimpleSelect
                        selected={filter}
                        onSelect={handleFilterSelect}
                        options={FILTER_OPTIONS}
                        placeholder="Status"
                        className={styles.toolbarInput}
                    />
                </div>
                {!!errorMessage && <AGMessage color="error" title={errorMessage} />}
                {loading && <Loader />}
                {!loading && !errorMessage && (
                    <>
                        <div className={styles.tableScroll}>
                            <AGTableWrapper>
                                <thead>
                                    <tr>
                                        <th>License plates</th>
                                        <th>First name</th>
                                        <th>Last name</th>
                                        <th>Email</th>
                                        <th>Phone</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayRows.map((slot) => {
                                        const displayStatus = deriveBulkSlotDisplayStatus(slot);
                                        const label = STATUS_LABEL[displayStatus];
                                        const statusColorClass = statusBadgeColorMap[displayStatus];
                                        return (
                                            <tr key={slot.uuid}>
                                                <td>{formatPlates(slot.cars_detail)}</td>
                                                <td>{slot.renter_first_name ?? '—'}</td>
                                                <td>{displayLastName(slot.renter_last_name, isDemoMode)}</td>
                                                <td className={isDemoMode ? 'blur' : ''}>{slot.renter_email ?? '—'}</td>
                                                <td className={isDemoMode ? 'blur' : ''}>{slot.renter_phone ?? '—'}</td>
                                                <td>
                                                    <div className={`${styles.statusTag} ${styles[statusColorClass]}`}>
                                                        {label}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </AGTableWrapper>
                            {displayRows.length === 0 && <p>No drivers match this filter.</p>}
                        </div>
                        {paged && totalPages > 1 && (
                            <div className={styles.paginationFooter}>
                                <Pagination
                                    currentPage={page}
                                    totalPages={totalPages}
                                    onPageChange={(p) => setPage(Number(p))}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
};

export default TenantBulkSlotsModal;
